import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, VariantProps } from 'class-variance-authority';
import { Icon } from '@antimatterhq/ui/components/icon';
import { cn } from '@antimatterhq/ui/lib/utils';
import { IconType } from '../icon/icon';

export const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 leading-5 whitespace-nowrap rounded-md text-sm ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border',
  {
    variants: {
      variant: {
        default:
          'bg-white dark:bg-black-100 text-grey-800 dark:text-grey-100 border-grey-200 dark:border-grey-650 hover:border-blue-700 dark:hover:border-blue-400 hover:text-blue-700 dark:hover:text-blue-400 hover:bg-blue-600/08 dark:hover:bg-blue-400/08',
        primary:
          'bg-blue-600 dark:bg-blue-500 text-white dark:text-grey-800 border-0 hover:bg-blue-700 dark:hover:bg-blue-400',
        tertiary:
          'bg-blue-600/0 dark:bg-blue-400/0 text-blue-600 dark:text-blue-500 border-0 hover:bg-blue-600/08 dark:hover:bg-blue-400/08 hover:text-blue-700 dark:hover:text-blue-400',
        danger:
          'bg-red-600/0 dark:bg-red-400/0 text-red-600 dark:text-red-500 border-red-600 dark:border-red-500 hover:bg-red-600/08 dark:hover:bg-red-400/08 hover:text-red-700 dark:hover:text-red-400 hover:border-red-700 dark:hover:border-red-400',
        dangerPrimary:
          'bg-red-600 dark:bg-red-500 text-white dark:text-grey-800 border-0 hover:bg-red-700 dark:hover:bg-red-400',
        warning: 'bg-yellow-500 text-white border-0 hover:bg-yellow-600',
        link: 'bg-transparent text-blue-600 dark:text-blue-500 border-0 hover:text-blue-700 dark:hover:text-blue-400 underline-offset-4 hover:underline',
        ghost:
          'bg-transparent text-grey-800 dark:text-grey-100 border-none hover:text-blue-700 dark:hover:text-blue-400',
      },
      size: {
        small: 'h-6',
        default: 'h-8',
        medium: 'h-10',
      },
      iconOnly: {
        true: 'aspect-square p-2.5',
        false: 'px-4',
      },
      block: {
        true: 'w-full',
        false: 'w-auto',
      },
      disabled: {
        true: 'bg-grey-100/0 dark:bg-grey-400/0 text-grey-400 dark:text-grey-500 border-grey-200 dark:border-grey-650 pointer-events-none opacity-50',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      block: false,
      iconOnly: false,
    },
  },
);

// Rest of the component remains the same
export type ButtonProps = VariantProps<typeof buttonVariants> &
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    asChild?: boolean;
    loading?: boolean;
    leftIcon?: IconType;
    rightIcon?: IconType;
    iconOnly?: boolean;
    block?: boolean;
    disabled?: boolean;
    animateIcon?: boolean;
  };

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading = false,
      leftIcon,
      rightIcon,
      iconOnly,
      block,
      children,
      disabled,
      animateIcon = false,
      type = 'button',
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';

    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, block, iconOnly, className }),
        )}
        ref={ref}
        disabled={disabled || loading}
        type={type}
        {...props}
      >
        {loading && (
          <Icon icon="lucide:loader-circle" className="animate-spin" />
        )}
        {!loading && leftIcon && (
          <Icon icon={leftIcon} className={animateIcon ? 'animate-spin' : ''} />
        )}
        {children}
        {!loading && rightIcon && (
          <Icon
            icon={rightIcon}
            className={animateIcon ? 'animate-spin' : ''}
          />
        )}
      </Comp>
    );
  },
);
Button.displayName = 'Button';
